import appConfig from '../configs/appConfig.json';

const isResolvedIp = (ip: string): boolean => !!ip && !ip.startsWith('unknown');

// Port to use when building the local URL from a resolved local_ip — parsed
// from appConfig.rag_server_local so it stays in sync with that config
// (falls back to 50053, the port rag_server_local has always used, if that
// config value is missing or unparseable).
const localPort = (): string => {
  try {
    return new URL((appConfig as any).rag_server_local).port || '50053';
  } catch {
    return '50053';
  }
};

/**
 * Picks which rag_server base URL a request should use.
 *
 * If the frontend's own external IP matches the Brunix node the primary AVS
 * is wired to, that "remote" Brunix server is actually us — same check
 * brunix-proxy-local-edge.js's relay already does (ownIp === targetIp) to
 * decide when to stop treating a target as remote. In that case, calling
 * out to the public rag_server and having it hairpin back in through our
 * own external IP won't work anyway (no inbound port open) — a local URL
 * reaches the same place directly. Otherwise, rag_server (the actual remote
 * host) is correct.
 *
 * When local, `localIp` (the Brunix node's own LAN ip, node_config.local_ip
 * from getPrimaryAvsBrunixNode()) is preferred over plain `localhost`:
 * `brunixIp === ownIp` only means both are behind the same public IP/NAT —
 * on a LAN with more than one machine behind it, the frontend and the
 * Brunix node can still be two different boxes, and `localhost:50053` would
 * hit the frontend's own machine instead of the actual Brunix host. The
 * resolved LAN ip reaches the right machine directly. Falls back to
 * appConfig.rag_server_local (plain localhost) when no local_ip was
 * resolved — same behavior as before this existed.
 */
export const resolveRagServer = (brunixIp: string, ownIp: string, localIp?: string): string => {
  if (isResolvedIp(brunixIp) && isResolvedIp(ownIp) && brunixIp === ownIp) {
    if (localIp) return `http://${localIp}:${localPort()}`;
    return (appConfig as any).rag_server_local || appConfig.rag_server;
  }
  return appConfig.rag_server;
};
